import SEO from "../components/SEO";
import { Link } from "react-router-dom";
import serverRouteContent from "../content/serverRouteContent.json";

const SITE_DESCRIPTION =
  "Independent UK prize competition transparency and odds comparison. Compare before you enter.";

function InfoPage({ kicker, title, description, path, children }) {
  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-8 py-16">
      <SEO title={title} description={description || SITE_DESCRIPTION} path={path} />
      <div className="overline text-[var(--cc-gold)] mb-2">{kicker}</div>
      <h1 className="font-display text-3xl sm:text-4xl font-semibold">{title}</h1>
      <div className="mt-8 space-y-6 text-[var(--cc-text-muted)] leading-relaxed">
        {children}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-xl font-semibold text-white mb-2">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function MailLink({ email, children = email }) {
  return (
    <a className="text-[var(--cc-gold)] hover:underline" href={`mailto:${email}`}>
      {children}
    </a>
  );
}

function GeneralContactBlock() {
  return (
    <p>
      Competition Checker is operated by QUOD-Tech Ltd. For general enquiries,
      contact <MailLink email="info@competitionchecker.com" />. For business or
      partnership enquiries, contact <MailLink email="info@quod-tech.com" />.
    </p>
  );
}

export function Contact() {
  return (
    <InfoPage
      kicker="Contact"
      title="Contact Competition Checker"
      description="Contact Competition Checker for general enquiries, operator data corrections, business enquiries and partnerships."
      path="/contact"
    >
      <p>
        Competition Checker is an independent UK prize competition comparison
        and transparency site. We do not operate prize draws, sell tickets,
        process entries, or handle operator customer service requests.
      </p>
      <GeneralContactBlock />
      <Section title="General enquiries">
        <p><MailLink email="info@competitionchecker.com" /></p>
      </Section>
      <Section title="Account and Premium support">
        <p><MailLink email="support@competitionchecker.com" /></p>
      </Section>
      <Section title="Business / partnerships / affiliates">
        <p><MailLink email="info@quod-tech.com" /></p>
      </Section>
      <Section title="Operator data corrections">
        <p><MailLink email="info@competitionchecker.com" /></p>
      </Section>
      <Section title="Company">
        <p>QUOD-Tech Ltd</p>
      </Section>
      <Section title="Operator and entry questions">
        <p>
          If you have entered a competition, need ticket support, or have a
          question about a draw, contact the relevant operator directly. Always
          check the operator's official terms before entering.
        </p>
      </Section>
    </InfoPage>
  );
}

export function PrivacyPolicy() {
  return (
    <InfoPage
      kicker="Privacy"
      title="Privacy Policy"
      description="Read how Competition Checker handles site analytics, cookies, affiliate referrals, advertising partners and privacy enquiries."
      path="/privacy-policy"
    >
      <p>Competition Checker is operated by QUOD-Tech Ltd. This notice explains personal data used by the public comparison service and by optional consumer accounts where enabled.</p>
      <Section title="Data we use">
        <p>Public browsing can create limited technical and outbound-link records such as the page or competition selected, destination, time, link context and browser user-agent. We do not require a consumer account to browse competitions.</p>
        <p>If accounts are enabled and you register, we use your chosen username, email address, an immutable internal user ID, a password hash where you set a password, verification and security timestamps, sessions, favourite operators and purpose-specific privacy choices. We do not ask for your postal address, telephone number, date of birth, gender or legal name for a basic account.</p>
        <p>If you choose Google sign-in, we verify a Google identity token and bind its stable subject to your Competition Checker account. We do not store Google's profile name or photograph, request Gmail or Contacts access, or treat a Google account as an administrator account.</p>
      </Section>
      <Section title="Purposes and lawful basis">
        <p>Account creation and requested account features are intended to be provided as a contract with the user. Security, abuse prevention, service integrity and limited affiliate-link measurement may rely on our legitimate interests where those interests are not overridden by your rights. Optional marketing, advertising or personalisation requires the relevant choice or consent.</p>
      </Section>
      <Section title="Cookies, analytics and advertising">
        <p>Strictly necessary storage supports authentication, session security and saving privacy choices. Optional service analytics and advertising or personalisation storage are off by default and are separately controlled. Advertising also remains disabled unless its technical configuration and consent-management boundary are ready.</p>
      </Section>
      <Section title="Affiliate links and referrals">
        <p>
          Competition Checker may earn commission from some outbound links where
          an operator has an approved affiliate or partner arrangement with us.
          Affiliate links are clearly labelled where used. Affiliate
          relationships do not influence scoring, ranking, odds calculations,
          operator trust notes, or whether a competition is shown.
        </p>
      </Section>
      <Section title="Advertising partners">
        <p>
          Competition Checker may work with third-party advertising partners,
          including Google AdSense. Those services can use cookies, web beacons,
          IP addresses or other identifiers for ad serving and measurement, so
          they are not loaded merely because an ownership-verification record is
          present. Advertising and personalisation remain subject to the site
          configuration and your applicable privacy choice.
        </p>
        <p>
          Read{" "}
          <a
            className="text-[var(--cc-gold)] hover:underline"
            href="https://policies.google.com/technologies/partner-sites"
            rel="noopener noreferrer"
            target="_blank"
          >
            How Google uses information from sites or apps that use our services
          </a>
          .
        </p>
      </Section>
      <Section title="Sharing and international transfers">
        <p>We use service providers for infrastructure and account email delivery, including Resend when configured. Google processes the identity sign-in you choose. Some providers may process data outside the UK under their contractual transfer safeguards. We do not sell or export individual account activity as an insight product. Any future external insight must be aggregate, contain no stable user identifier and suppress small groups.</p>
      </Section>
      <Section title="Retention, export and deletion">
        <p>One-time verification and recovery records and sessions expire automatically at their configured expiry. Account data is kept while the account is active. An authenticated export excludes passwords, hashes, tokens and other users. Account deletion revokes sessions, releases the real email and removes optional account records, while a pseudonymous internal-ID tombstone and minimal security or legal audit may be retained for a documented period. That tombstone is not described as anonymous.</p>
        <p><Link className="text-[var(--cc-gold)] underline" to="/delete-account">Delete your account or request deletion without the app</Link>.</p>
      </Section>
      <Section title="Your rights">
        <p>You may ask to access, correct, erase or restrict personal data, object where processing relies on legitimate interests, withdraw an optional choice and request a portable account export where applicable. You may also complain to the UK Information Commissioner's Office.</p>
      </Section>
      <Section title="Privacy questions">
        <p>
          For privacy questions or rights requests, contact{" "}
          <MailLink email="support@competitionchecker.com" />. Please include
          enough detail for us to understand the page or data you are asking
          about.
        </p>
      </Section>
    </InfoPage>
  );
}

export function Terms() {
  return (
    <InfoPage
      kicker="Terms"
      title="Terms of Use"
      description="Read the terms governing Competition Checker's independent competition information, calculations and public comparison service."
      path="/terms"
    >
      <p>
        Competition Checker provides independent competition information and
        estimated calculations based on publicly available or manually entered
        data. We do not operate prize draws, sell tickets, process entries, or
        guarantee outcomes.
      </p>
      <Section title="Independent information">
        <p>
          Prices, ticket caps, odds-style calculations, draw dates, prizes and
          operator details can change. Always check the operator's official
          terms and competition page before entering.
        </p>
      </Section>
      <Section title="No guarantee of outcomes">
        <p>
          Competition Checker does not promise winnings, improved chances, or
          financial returns. Our scoring is designed to make public information
          easier to compare, not to encourage spending.
        </p>
      </Section>
      <Section title="Commercial relationships">
        <p>
          Affiliate or advertising relationships do not affect scoring, odds
          calculations, trust checks, transparency verdicts, sorting, or
          ranking.
        </p>
      </Section>
      <Section title="Consumer accounts">
        <p>When consumer accounts are enabled, you must provide a valid email, protect your sign-in details and use the service lawfully. You can sign in with your username or email; an internal user ID keeps account relationships stable if your email changes.</p>
        <p>We may suspend an account to protect users or the service. You may export your account data or delete your account through the authenticated controls. Consumer accounts do not create a Premium subscription or payment obligation unless a separate subscription service is offered and accepted later.</p>
      </Section>
      <Section title="Privacy choices">
        <p>Terms acceptance is required for an account. Marketing, service analytics, advertising or personalisation and anonymous insight-sharing choices are separate and are not bundled into these Terms.</p>
      </Section>
      <Section title="Contact">
        <GeneralContactBlock />
      </Section>
    </InfoPage>
  );
}

export function AffiliateDisclosure() {
  return (
    <InfoPage
      kicker="Disclosure"
      title="Affiliate Disclosure"
      description="Learn how Competition Checker labels affiliate links while keeping scoring, ranking, odds and operator trust checks independent."
      path="/affiliate-disclosure"
    >
      <p>
        Competition Checker may earn commission from some outbound links where
        an operator has an approved affiliate or partner arrangement with us.
        Affiliate links are clearly labelled where used.
      </p>
      <Section title="Independence">
        <p>
          Affiliate relationships do not influence scoring, ranking, odds
          calculations, operator trust notes, or whether a competition is shown.
        </p>
        <p>
          Trusted operator status and affiliate partner status are separate.
          An operator can be tracked for transparency without being an
          affiliate partner, and an affiliate relationship does not make an
          operator trusted.
        </p>
      </Section>
      <Section title="Data integrity">
        <p>
          We do not invent prices, ticket caps, odds, draw dates, or prize
          details for commercial reasons. When data is missing or unsafe, the
          site should flag that rather than presenting unsupported odds.
        </p>
      </Section>
      <Section title="Contact">
        <GeneralContactBlock />
      </Section>
    </InfoPage>
  );
}

export function HowWeScore() {
  return (
    <InfoPage
      kicker="Methodology"
      title="How We Score"
      description="How Competition Checker evaluates odds, value, transparency, trust and urgency without guessing missing evidence."
      path="/how-we-score"
    >
      <p>
        Competition Checker compares public competition information in a
        consistent, explainable way. Where available, we use ticket price,
        maximum entries, draw date, prize category, operator, and verification
        status.
      </p>
      <Section title="Odds-style calculations">
        <p>
          Odds-style calculations depend on trusted ticket cap or maximum entry
          data. If the cap is missing, unsafe, or only a per-user limit, the
          site should not present odds as ready.
        </p>
      </Section>
      <Section title="Score inputs">
        <p>
          The public score combines odds, value, transparency, trust and urgency
          signals. Missing or weak data reduces confidence rather than being
          guessed.
        </p>
      </Section>
      <Section title="Commercial independence">
        <p>
          Affiliate status does not influence scoring, ranking, odds
          calculations, operator trust notes, or whether a competition is
          shown. Trust checks and transparency verdicts remain independent from
          commercial relationships.
        </p>
      </Section>
    </InfoPage>
  );
}

export function ResponsibleParticipation() {
  return (
    <InfoPage
      kicker="Responsible participation"
      title="Responsible Participation"
      description="Guidance for using UK prize competition information cautiously, checking operator terms and keeping spending under control."
      path="/responsible-participation"
    >
      <p>
        Competition Checker is built for transparency and consumer caution. It
        is not gambling advice and it is not a promise that a competition is a
        good use of your money.
      </p>
      <Section title="Independent information service">
        <p>
          We provide information and comparison only. We do not accept stakes,
          process entries or award prizes. Always review the operator's official
          terms and free-entry route before deciding whether to enter.
        </p>
      </Section>
      <Section title="Maximum-pool odds, not a promise">
        <p>
          For a supported one-winner mechanic, one ticket is compared with the
          trusted maximum ticket allocation. The result is a conservative
          maximum-pool estimate, not a prediction of final sales or a guarantee
          of winning. Multiple entries change probability but do not remove uncertainty.
        </p>
        <p>
          Read the practical guide to{" "}
          <Link className="text-[var(--cc-gold)] hover:underline" to="/guides/how-competition-odds-are-calculated">
            how competition odds are calculated
          </Link>
          .
        </p>
      </Section>
      <Section title="Before entering">
        <p>
          Only spend what you can comfortably afford. Prize competitions are
          not a reliable way to make money, and odds can be long even when a
          competition looks attractive.
        </p>
        <p>
          Check ticket price, maximum entries, draw date, free-entry route and
          operator terms before entering.
        </p>
      </Section>
      <Section title="Keep control">
        <p>
          Do not chase losses or keep entering because of previous spend. If
          competition spending feels difficult to control, consider taking a
          break and seeking support from a trusted person or a specialist help
          service.
        </p>
      </Section>
    </InfoPage>
  );
}

export function CookiePolicy() {
  return (
    <InfoPage
      kicker="Privacy"
      title="Cookie Policy"
      description="Read how Competition Checker uses essential browser storage and how optional analytics or advertising consent would be managed."
      path="/cookie-policy"
    >
      <p>Competition Checker separates necessary storage from optional analytics and advertising storage. Optional categories are off by default.</p>
      <Section title="Strictly necessary">
        <p>The site stores your privacy choice locally. If consumer accounts are enabled, a secure HttpOnly session cookie supports sign-in and account security. These functions do not contain your plaintext password and are not used for advertising.</p>
      </Section>
      <Section title="Service analytics">
        <p>Service analytics is a separate choice. It remains off unless you enable it and a qualifying aggregate measurement service is configured. It must not be repurposed for advertising or individual behavioural profiling.</p>
      </Section>
      <Section title="Advertising and personalisation">
        <p>Advertising or personalisation storage is off by default. An advertising slot requires the advertising configuration, consent-management readiness and your explicit choice before it can render.</p>
      </Section>
      <Section title="Your choices">
        <p>Use the Privacy choices control in the site footer at any time. Rejecting optional storage is as available as enabling it. Account holders can also manage marketing email and anonymous insight-sharing separately under My Account.</p>
      </Section>
    </InfoPage>
  );
}

export function HowItWorks() {
  const content = serverRouteContent["/how-it-works"];
  return (
    <InfoPage
      kicker="Methodology"
      title="How Competition Checker Works"
      description="Learn how to compare competitions, understand missing odds, check update timing and use Competition Checker on your phone."
      path="/how-it-works"
    >
      <p>{content.lede}</p>
      {content.sections.map((section) => (
        <Section key={section.heading} title={section.heading}>
          {section.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          {section.links?.length > 0 && (
            <nav className="flex flex-col gap-2" aria-label={`${section.heading} related links`}>
              {section.links.map((link) => (
                <Link key={link.href} className="text-[var(--cc-gold)] hover:underline" to={link.href}>
                  {link.label}
                </Link>
              ))}
            </nav>
          )}
        </Section>
      ))}
    </InfoPage>
  );
}

export function DataAccuracy() {
  return (
    <InfoPage
      kicker="Transparency"
      title="Data Accuracy and Update Timing"
      description="Understand Competition Checker's source evidence, update timing and last-known-good safeguards when operator pages change."
      path="/data-accuracy"
    >
      <p>
        Operator pages can change between scheduled checks. We retain field
        provenance and may preserve the last known good public record when a
        source scan is incomplete rather than replacing it with uncertain data.
      </p>
      <Section title="Limitations">
        <p>
          Always confirm current prices, availability, closing dates and terms
          on the operator's official page before entering.
        </p>
      </Section>
      <Section title="What trusted means">
        <p>
          Money requires explicit GBP evidence. A total ticket cap must describe
          the competition-wide allocation; a per-person limit, sold percentage
          or remaining-stock value is not silently substituted. Images and source
          URLs are also checked under operator-specific rules.
        </p>
      </Section>
      <Section title="Corrections">
        <p>
          If a material field differs from the live source, email{" "}
          <MailLink email="info@competitionchecker.com" /> with both page URLs.
          Corrections should improve the underlying evidence rather than replace
          one unexplained value with another.
        </p>
      </Section>
    </InfoPage>
  );
}

export function EditorialMethodology() {
  return (
    <InfoPage
      kicker="Editorial"
      title="Editorial and Scoring Methodology"
      description="How Competition Checker separates operator-supplied facts from independent calculations, commentary and commercial relationships."
      path="/editorial-methodology"
    >
      <p>
        Competition Checker separates operator-supplied facts from its own
        calculations and commentary. Affiliate or future advertising
        relationships do not change scoring, ranking, odds or trust checks.
      </p>
      <Section title="Evidence first">
        <p>
          Public values require traceable evidence and operator-specific trust
          rules. Review flags remain visible when evidence is partial.
        </p>
      </Section>
      <Section title="Deterministic analysis">
        <p>
          The same trusted inputs produce the same calculations. Editorial text
          explains those outputs and their limitations; it must not add an
          unsupported price, cap, prize value, cash alternative, status or odds figure.
        </p>
      </Section>
      <Section title="Independence and review">
        <p>
          Tracked operator membership is not endorsement. Affiliate labels and
          advertising relationships stay outside the scoring and public
          eligibility rules. Material methodology changes remain reviewable in
          the application's source and tests.
        </p>
        <p>
          See also{" "}
          <Link className="text-[var(--cc-gold)] hover:underline" to="/how-we-score">How We Score</Link>
          {" "}and{" "}
          <Link className="text-[var(--cc-gold)] hover:underline" to="/data-accuracy">Data Accuracy</Link>.
        </p>
      </Section>
    </InfoPage>
  );
}
